import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { studioIdeas, studioProjects } from '@/lib/api';

interface RoiTemplate {
  avg_hours_saved_monthly?: number;
  revenue_potential_monthly?: number;
  client_retention_impact?: string;
}

interface Idea {
  id: string;
  title: string;
  description: string;
  verticals: string[];
  difficulty: string;
  estimated_build_minutes: number;
  roi_template?: RoiTemplate;
  related_starter_id?: string | null;
}

const DIFFICULTY_COLOR: Record<string, string> = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  hard: 'bg-red-100 text-red-700',
};

export function IdeaLibrary() {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [buildingId, setBuildingId] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const data = await studioIdeas.list();
        setIdeas((data.ideas ?? []) as Idea[]);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  async function buildFromIdea(idea: Idea) {
    setBuildingId(idea.id);
    try {
      const project = await studioProjects.create({
        name: idea.title,
        starter_id: idea.related_starter_id ?? undefined,
      });
      navigate(`/builder/${project.id}`);
    } finally {
      setBuildingId(null);
    }
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Idea Library</h1>
        <p className="text-muted-foreground mt-1">AI-curated app ideas matched to your MSP profile</p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      ) : ideas.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl">
          <p className="text-muted-foreground mb-3">Complete onboarding to get personalized ideas.</p>
          <button
            onClick={() => navigate('/onboarding')}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm"
          >
            Start Onboarding
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {ideas.map((idea) => (
            <div key={idea.id} className="border border-border rounded-xl p-5 bg-card hover:border-primary/50 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-foreground">{idea.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${DIFFICULTY_COLOR[idea.difficulty] ?? 'bg-muted text-muted-foreground'}`}>
                      {idea.difficulty}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{idea.description}</p>
                  {idea.roi_template && (
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      {idea.roi_template.avg_hours_saved_monthly && (
                        <span className="text-green-600">⏱ {idea.roi_template.avg_hours_saved_monthly}h/mo saved</span>
                      )}
                      {idea.roi_template.revenue_potential_monthly && (
                        <span className="text-green-600">💰 +${idea.roi_template.revenue_potential_monthly}/mo potential</span>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => void buildFromIdea(idea)}
                  disabled={buildingId === idea.id}
                  className="flex-shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-medium"
                >
                  {buildingId === idea.id ? 'Building…' : 'Build This →'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
